import { InfoBar, SidebarOverlay, Tab } from "@etframework/ui.components";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { IconLoading } from "../../components/Loading/IconLoading";
import { ActivityContainer } from "../../components/OverlayComponents/ActivityContainer";
import { CommentsContainer } from "../../components/OverlayComponents/CommentsContainer";
import { CustomFieldsContainer } from "../../components/OverlayComponents/CustomFieldsContainer";
import { TaskConsistentDataContainer } from "../../components/OverlayComponents/TaskConsistentDataContainer";
import { TaskInformationTab } from "../../components/OverlayComponents/TaskInformationTab";
import { TaxReturnInformation } from "../../components/OverlayComponents/TaxReturnInformation";
import { useAddTaskCollaborator } from "../../data/mutations/taskMutations";
import { useCommentsCount } from "../../data/queries/commentQueries";
import { useCustomFieldGroupByTaskTypeId } from "../../data/queries/customFieldGroupQueries";
import { useTask } from "../../data/queries/taskQueries";
import { useClickOutside } from "../../hooks/useClickOutside";
import { useDueDatesAuth } from "../../hooks/useDueDatesAuth";
import useEvent from "../../hooks/useEvent";
import useRolePermissionStore from "../../store/rolePermissionStore";
import Enums from "../../types/enums";
import { canModifyTask } from "../../utils/helpers";

export const TaskOverlay = () => {
  const { t } = useTranslation(["Overlay"], {
    useSuspense: false
  });

  //States
  const [taskId, setTaskId] = useState(null);
  const [, setSearchParams] = useSearchParams();

  const visualStatus = useRef({
    extendedDate: false,
    isOpening: false
  });

  const { data: task, isPending: isTaskPending } = useTask(taskId);
  const { data: customFieldGroups } = useCustomFieldGroupByTaskTypeId(
    task?.TaskType?.Id,
    false
  );

  const { data: commentsCount } = useCommentsCount(taskId);
  const rolePermissions = useRolePermissionStore(
    (state) => state.rolePermissions
  );

  //Mutations
  const { mutate: addTaskCollaborator } = useAddTaskCollaborator();

  const { UserId } = useDueDatesAuth();

  const canModifyTasks = canModifyTask(task, UserId, rolePermissions);

  const queryClient = useQueryClient();

  //Ids
  const ids = {
    main: "dd_task-overlay",
    assignee: "dd_task-overlay_assignee-input",
    client: "dd_task-overlay_client-input",
    name: "dd_task-overlay_name-input",
    stage: "dd_task-overlay_stage-input",
    description: "dd_task-overlay_description-input",
    dueDate: "dd_task-overlay_due-date-input",
    startDate: "dd_task-overlay_start-date-input",
    tags: "dd_task-overlay_tags-input",
    taxReturnInfo: {
      template: "dd_task-overlay_tax-return-info_template-input",
      taxYear: "dd_task-overlay_tax-return-info_tax-year-input",
      filingStatus: "dd_task-overlay_tax-return-info_filing-status-input",
      agency: "dd_task-overlay_tax-return-info_agency-input",
      taxDeadline: "dd_task-overlay_tax-return-info_tax-deadline-input",
      extendedTaxDeadline:
        "dd_task-overlay_tax-return-info_extended-tax-deadline-input",
      periodEnd: "dd_task-overlay_tax-return-info_period-end-input"
    },
    moreActionsBtn: "dd_task-overlay_more-actions-button",
    deleteBtn: "dd_task-overlay_delete-task-button",
    copyTaskLink: "dd_task-overlay_copy-task-url-button"
  };

  //Refs
  const tooltipRef = useRef();
  const overlayRef = useRef();
  const tabRef = useRef(null);

  //Hooks
  const { Events, toggleEvent, useRegisterCustomEvent } = useEvent();

  //Handlers
  const handleOverlayClose = () => {
    if (overlayRef.current) tooltipRef?.current?.close();
    setSearchParams((p) => {
      p.delete("taskId");
      return p;
    });
  };

  useClickOutside(ids.main, (e) => {
    if (overlayRef?.current?.getComponentRef().isOpen) {
      if (
        !visualStatus.current.collaborators &&
        !visualStatus.current.extendedDate &&
        !visualStatus.current.isOpening &&
        !e?.target?.closest(".e-rte-img-dialog")
      ) {
        overlayRef.current?.hide();
      }
      visualStatus.current.isOpening = false;
    }
  });

  useRegisterCustomEvent(Events.CloseTaskOverlay, () => {
    overlayRef.current?.hide();
  });

  useRegisterCustomEvent(Events.TaskOverlay, (e) => {
    if (overlayRef.current) {
      const { taskId, onOpen } = e.detail;
      visualStatus.current.isOpening = true;
      setTaskId(taskId);
      setSearchParams((p) => {
        p.set("taskId", taskId);
        return p;
      });
      overlayRef.current.show();
      onOpen?.();
    }
  });

  const TaskConsistentDataTemplate = () => {
    const updateCollaborators = () => {
      var collaboratorsUpdated = {
        TaskId: task.Id,
        UserId: UserId
      };

      addTaskCollaborator(
        { collaborators: collaboratorsUpdated },
        {
          onSuccess: (response) => {
            queryClient.invalidateQueries(["get-task", task.Id]);
            toggleEvent(Events.RefreshTaskGrid);
            toast.success(response.Message, { position: "bottom-left" });
          },
          onError: () => {}
        }
      );
    };

    return (
      <div className="task-overlay-content left-side p-4 et-of-auto">
        {!canModifyTasks && (
          <div className="mb-2">
            <InfoBar
              variant="information"
              title={t("CollaboratorsMsg")}
              rounded
              actions={[
                {
                  onClick: () => updateCollaborators(),
                  label: t("CollaboratorsMsgAction"),
                  className: "btn btn-tertiary"
                }
              ]}
            />
          </div>
        )}
        <TaskConsistentDataContainer
          ids={ids}
          task={task}
          rolePermissions={rolePermissions}
          canModifyTasks={canModifyTasks}
          overlayRef={overlayRef}
        />

        {task?.TaskType?.Type == Enums.TaskType.TaxReturn && (
          <TaxReturnInformation
            ids={ids}
            canModifyTasks={canModifyTasks}
            task={task}
            visualStatusRef={visualStatus}
          />
        )}

        {customFieldGroups != null && (
          <div>
            {customFieldGroups.map((customFieldGroup, index) => (
              <div className="my-4" key={index}>
                <CustomFieldsContainer
                  taskCustomFields={task?.CustomFields}
                  customFieldGroup={customFieldGroup}
                  taskId={task?.Id}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const CommentTemplate = () => {
    return (
      <div className="task-overlay-content">
        <CommentsContainer task={task} />
      </div>
    );
  };

  const ActivityTemplate = () => {
    return (
      <div className="task-overlay-content">
        <ActivityContainer task={task} />
      </div>
    );
  };

  useEffect(() => {
    const handleOverlayToggle = () => {
      tabRef?.current?.setSelectedTab(0);
    };

    document.addEventListener(Events.TaskOverlay.Event, handleOverlayToggle);

    return () => {
      document.removeEventListener(
        Events.TaskOverlay.Event,
        handleOverlayToggle
      );
    };
  }, []);

  return (
    <div className="h-auto w-auto">
      <SidebarOverlay
        ref={overlayRef}
        id={ids.main}
        width="1172px"
        close={handleOverlayClose}
        closeOnDocumentClick={false}
      >
        {isTaskPending ? (
          <div className="d-flex align-items-center justify-content-center h-100 w-100">
            <IconLoading />
          </div>
        ) : (
          <div className="task-overlay px-3 pb-3">
            <div className="d-flex">
              <div className="col-7">
                <TaskConsistentDataTemplate />
              </div>
              <div className="col-5">
                <Tab
                  ref={tabRef}
                  headerPlacement={"Right"}
                  cssClass="orientation-tab"
                  className="flex-row"
                  tabNames={[
                    "",
                    `<div class="comment-count">${
                      commentsCount > 99 ? "99+" : commentsCount
                    }</div>`,
                    ""
                  ]}
                  tabIcons={[
                    "fa-regular fa-circle-info",
                    "fa-regular fa-comment",
                    "fa-regular fa-notebook"
                  ]}
                  tabComponents={[
                    TaskInformationTab,
                    CommentTemplate,
                    ActivityTemplate
                  ]}
                />
              </div>
            </div>
          </div>
        )}
      </SidebarOverlay>
    </div>
  );
};

TaskOverlay.displayName = "Task Overlay";
